define((require) => {
    "use strict";

    const $                          = require("jquery");
    const _                          = require("underscore");
    const Backbone                   = require("backbone");
    const DeleteVariantAction        = require("ui/actions/DeleteVariantAction");
    const EditBehavior               = require("ui/behaviors/EditBehavior");
    const EditorEvents               = require("shared/services/EditorEvents");
    const ImprovedView               = require("editor/zbone/ImprovedView");
    const LoggedInUserState          = require("shared/ui/user/LoggedInUserState");
    const NavigateToExperienceAction = require("ui/actions/NavigateToExperienceAction");
    const Permissions                = require("shared/security/Permissions");
    const ResourceCombined           = require("shared/util/ResourceCombined");
    const SpinnerView                = require("shared/ui/common/SpinnerView");
    const SyncManager                = require("shared/services/sync/SyncManager");
    const template                   = require("editor/ui/experience/editor/GroupNavView.tpl");
    const Tooltip                    = require("ui/common/Tooltip");
    const Window                     = require("shared/services/Window");
    const ValidateBehavior           = require("ui/behaviors/ValidateBehavior");
    const {isBlank}                  = require("shared/util/StringUtils");
    const ViewScope                  = require("shared/ui/common/ViewScope");
    const ExperienceModel            = require("shared/ui/experience/ExperienceModel");
    const WarningMessages            = require("util/WarningMessages");
    const Configuration              = require("shared/services/Configuration");
    const Storage                    = require("shared/services/Storage");

    var MAX_NUMBER_OF_ALLOWED_VARIANTS = 12;

    /**
     * A view that represents all variant tabs for a specific breakpoint group.
     *
     * @cfg {GroupModel} model The BreakpointGroup model
     *
     * @class GroupNavView
     */
    return ImprovedView.extend({

        template,

        _isSpectator: null,

        _currentExperience: null,

        className: "experience-variant-control",

        experience: null,
        _designFileUIEnabled: null,

        _isNewDashboardExperience: false,

        ui: {
            pushGroupBtn:   ".push-group-btn",
            addVariantIcon: ".add-variant-icon",
            experienceImportApiKey:  ".import-key",
            importAction:            ".btn-import-action",
            variantsRegion: ".variants-region",
            deleteBtn:      ".delete-btn",
            firstLine:      ".first-line .group-variants",
            secondLine:     ".second-line",
            loadingSpinner: ".loading-spinner",
            parameterInputs: ".parameter-inputs input"
        },

        templateHelpers() {
            return {
                entries: this.model.getUniqueEntries(),
                currentVariant: this._currentExperience,
                spectator: this._isSpectator,
                designFileUIEnabled: this._designFileUIEnabled,
                hasMaxVariants: this._hasMaxVariants,
                groupType: this.model.getDashedType(),
                isNewDashboardExperience: this._isNewDashboardExperience
            };
        },

        events: {
            "click @ui.addVariantIcon": "addVariant",
            "click @ui.pushGroupBtn": "_pushGroup",
            "click .publish-btn": "_goToPublish",
            "click @ui.importAction": "_importActionClickHandler",
            "click .delete-btn": "_onDeleteVariantClicked",

            "click .variant-icon"(event) {
                if (this._isBreakpointDisabledOnEmailMode()) return;

                var experienceId = event.currentTarget.getAttribute("data-experience-id");
                var eventProperties = {
                    experienceId: experienceId,
                    groupId: this.model.get("id"),
                    groupVersion: this.model.get("version"),
                    groupPushedVersion: this.model.get("pushedVersion"),
                    groupType: this.model.get("type"),
                    projectId: this.options.project && this.options.project.get("id"),
                    triggerOrigin: "VariantIcon"
                };

                EditorEvents.trigger(EditorEvents.constants.SWITCH_VARIANT, eventProperties);

                this.navigateToExperience(experienceId);
                event.preventDefault();
            }
        },

        bindings: {
            ".import-key": "experienceImportApiKey",
            ".notification-box": {
                observe: ["experienceImportApiKey"],
                updateView: false,
                visible: function(values) {
                    return values[0] && values[0].length >= 0 && values[1] && values[1].length >= 0;
                }
            },
            ".group-name-input": {
                observe: "name",
                updateMethod: "text",
                escape: false,
                events: ["blur"]
            }
        },

        behaviors: {
            validateBehavior: {
                behaviorClass: ValidateBehavior,
                inputValidation: {
                    ".import-key": {
                        validateAfter: "beforeSubmit",
                        model:     true,    // mark as bound to model, so make sure we cancel pending save requests on error!
                        validator: function(key) {
                            return isBlank(key) || /^\w{24,24}$/.test(key); // at least 24 word characters (upper/lowercase letters + digits)
                        },
                        errorText: function() {
                            return "Not a valid Experience Id";
                        }
                    }
                }
            },
            editBehavior: {
                behaviorClass: EditBehavior,
                container: ".name-container",
                editFields: [{
                    input: ".group-name-input",
                    save(value) {
                        var eventProperties = {
                            groupId: this.model.get("id"),
                            groupType: this.model.get("type"),
                            groupVersion: this.model.get("version"),
                            groupPushedVersion: this.model.get("pushedVersion"),
                            triggerOrigin: "EditGroupNameInputField",
                            quickView: false,
                            projectId: this.options.project && this.options.project.get("id"),
                            updatedProperty: "name",
                            updatedValue: value
                        };

                        EditorEvents.trigger(EditorEvents.constants.UPDATE_GROUP, eventProperties);

                        this.model.set("name", value);
                    }
                }]
            }
        },

        modelEvents: {
            "change:name"() {
                this.model.save();
            }
        },

        initialize() {
            this._currentExperience = this.options.experience;
            this._isSpectator = Permissions.Privileges.CREATE.cannot();
            this._hasMaxVariants = this.model.getNumOfAssets() > MAX_NUMBER_OF_ALLOWED_VARIANTS;
            const settings = LoggedInUserState.get("companySettings");
            this._designFileUIEnabled = settings.get("designFileUIEnabled");
            this._isNewDashboardExperience = this.model.get('id').startsWith('C3_');
            _.bindAll(this, "_renderAndArrangeTabs", "_arrangeTabs", "_updatePushButtonAndArrangeTabs");

            this.listenTo(this._currentExperience, "change", this._updatePushButtonAndArrangeTabs);
            this.listenTo(this._currentExperience, "change:emailModeEnabled", this._isBreakpointDisabledOnEmailMode);
            this._resizeListener = Window.on("resize", this._arrangeTabs); // already bound to this above!
        },
        _importActionClickHandler(event) {
            ViewScope.register(this, this);
            event.preventDefault();
            Tooltip.remove(this.ui.importAction);
            const experienceId = this.ui.experienceImportApiKey.val();

            if (experienceId && this.inputValidation()) {
                try {
                    this._currentExperience = new ExperienceModel({id: experienceId}).fetch().then((experience) => {  // this.model has the data after I fetch it
                        return this.addVariantFromOtherExperience(experience);
                    });
                } catch (e) {
                    alert("Not a valid Experience Id!!:" + experienceId);
                }
            }
        },

        onRender() {
            this._updatePushButton();
        },

        /**
         * Re-render the view and arrange the tabs. Note: this method is called as a promise callback, so we need
         * to check whether it is closed.
         *
         * @private
         */
        _renderAndArrangeTabs() {
            if (!this.isClosed) {
                this.render();
                this._arrangeTabs();
            }
        },

        _updatePushButtonAndArrangeTabs() {
            // Hack: Have to wait for the css-transition on the push group button to begin,
            // otherwise the calculation in arrangeTabNavigation will go wrong.
            _.delay(this._arrangeTabs, 100);

            return this._fecthAndUpdatePushButton();
        },

        _isBreakpointDisabledOnEmailMode() {
            const emailModeEnabled = this._currentExperience.get("emailModeEnabled");
            if (emailModeEnabled) {
                this.ui.addVariantIcon.toggleClass("disabled", emailModeEnabled);
                Tooltip.create(this.ui.addVariantIcon, {
                    content: "Email mode does not support breakpoint variants. Disable Email Mode to create a breakpoint.",
                    html: true,
                    // This seems necessary to avoid Bootstrap (transition) specific errors
                    // when removing the tooltip?
                    animation: false
                });
                return true;
            }
            this.ui.addVariantIcon.toggleClass("disabled", emailModeEnabled);
            Tooltip.remove(this.ui.addVariantIcon);
            return false;
        },

        onShow() {
            SyncManager.promise(this.model.getSortedAssets()).done(this._updatePushButtonAndArrangeTabs);
            SyncManager.onSync({
                context:  this,
                target:   this.model,
                method:   "save",
                callback: this._renderAndArrangeTabs
            });

            this._isBreakpointDisabledOnEmailMode();
            this._arrangeTabs();
            this._addExperienceImportTooltip();
        },

        // If there is no room for the whole navigation in one line, we render the tabnavigation on a separate line
        _arrangeTabs() {
            this.ui.firstLine.show();
            this.ui.secondLine.hide();

            var variantsRegionHeight = this.options.editorNavBarElement.height(),
                tooNarrow = variantsRegionHeight > 45;

            if (tooNarrow) {
                this.ui.firstLine.hide();
                this.ui.secondLine.show();
            }
        },

        _fecthAndUpdatePushButton() {
            // For simplicity, we fech the group data in order to get the latest state - remember
            // that *this._currentExperience* is not directly referenced by the group, only referenced by ID in the form of "metadata".
            return this.model.fetch().always(this._updatePushButton.bind(this));
        },

        /**
         * @private
         * Initialize tooltip for copy experience button
         */
        _addExperienceImportTooltip() {
            Tooltip.create(this.ui.importAction, {
                content: "Copies the selected experience into this Breakpiont group.<br/>",
                html: true,
                animation: false
            });
        },

        /**
         * @private
         * Update the push button based on the states of Group model and its Experience variants.
         */
        _updatePushButton() {
            if (this._isNewDashboardExperience) return;
            var button = this.ui.pushGroupBtn,
                pushingAssets = this.toBePushedAssets(),
                hasPushingAssets = !!_.size(pushingAssets),
                isPublisher = Permissions.Privileges.PUBLISH.can();

            if (!this.model.hasNonPushedChanges() && !hasPushingAssets || !isPublisher) {
                button.addClass("disabled").attr("disabled", "disabled");
            } else {
                button.removeClass("disabled");
                button[0].removeAttribute("disabled");

                Tooltip.remove(button);
                Tooltip.create(button, {
                    content: "The following items will be pushed: <br/>" + this._createPushedInfoTooltip(pushingAssets),
                    html: true,
                    // This seems necessary to avoid Bootstrap (transition) specific errors
                    // when removing the tooltip?
                    animation: false
                });
            }
        },

        /**
         * Creates the pushed information tooltip for the push group button.
         * @param {Object} pushingAssets the assets that need to be pushed
         * @param {Boolean} identifierInFront   whether or not the identifier string for the model
         *                                      (breakpoint no or localization type) should be placed in the front
         * @returns {String} the pushed info tooltip for the push group button.
         */
        _createPushedInfoTooltip(pushingAssets) {
            var composedTips = "";

            _.each(pushingAssets, (asset, key) => {
                var tip = _.escape(asset.get("name"));
                composedTips += (this.options.identifierInFront ? key + ": " + tip : tip + " (" + key + ")") + "<br/>";
            });

            // If there is a change to the group, we just inform users by list "Group data".
            if (this.model.hasNonPushedChanges()) {
                composedTips += "Group data";
            }

            return composedTips;
        },

        navigateToExperience(experienceId) {
            var sceneNumber = this.options.viewModel.get("sceneNumber");

            new NavigateToExperienceAction(sceneNumber)
                .execute(experienceId);
        },

        // Add variant an abstract method, should be implemented in the sub-classes
        addVariant() {
            throw new Error("addVariant must be implemented in sub class");
        },
        addVariantFromOtherExperience() {
            throw new Error("addVariantFromOtherExperience must be implemented in sub class");
        },
        _onDeleteVariantClicked(event) {
            event.preventDefault();
            event.stopPropagation();

            var $deleteButton = $(event.currentTarget);

            if ($deleteButton.hasClass("disabled")) {
                return;
            }

            var experienceId = $deleteButton.attr("data-experience-id"),
                variantModel = this.model,
                currentExperience = this._currentExperience,
                project = this.project,
                experiences = this._experiences;

            $deleteButton.hide();

            var $spinnerEl = $("<div></div>").addClass("loading-spinner");
            $deleteButton.after($spinnerEl);

            var spinnerView = new SpinnerView({
                el: $spinnerEl,
                lines: 8,
                length: 2,
                width: 2,
                radius: 2
            });
            spinnerView.render();

            var onMessageFetched = () => {
                spinnerView.destroy();
                $deleteButton.show();
            };

            WarningMessages.fetchDeleteExperienceMessage(variantModel, variantModel.get("type"))
                .then((warningMessageText) => {
                    onMessageFetched();

                    _.delay(() => {
                        if (confirm(warningMessageText)) {
                            new DeleteVariantAction(variantModel, experienceId, project)
                                .execute()
                                .done(() => {
                                    // If deleting the current experience, navigate to the first variant
                                    if (currentExperience.get("id") === experienceId) {
                                        return new NavigateToExperienceAction().execute(variantModel.getFirstAsset(experiences));
                                    }
                                });
                        }
                    }, 50);
                })
                .fail(() => {
                    onMessageFetched();
                });
        },

        _pushGroup() {
            Tooltip.remove(this.ui.pushGroupBtn);

            const settings = LoggedInUserState.get("companySettings");

            return this.model.push(true) // true = with assets
                .then(() => {
                    const token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token");
                    fetch(
                        `${Configuration.creatorSettingApiBaseUrl}/v1/buildSsr?companyId=${this.model.get("companyId")}&groupId=${this.model.get("id")}`,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`
                            },
                            method: "GET"
                        }
                    )
                        .catch();
                    var eventProperties = {
                        groupId: this.model.get("id"),
                        groupType: this.model.get("type"),
                        groupVersion: this.model.get("version"),
                        groupPushedVersion: this.model.get("pushedVersion"),
                        projectId: this.options.project && this.options.project.get("id"),
                        triggerOrigin: "PushGroupButton"
                    };

                    EditorEvents.trigger(EditorEvents.constants.PUSH_GROUP, eventProperties);

                    return this.options.pushed && this.options.pushed.fetchPushedOnly();
                })
                .then(() => {
                    return this._updatePushButtonAndArrangeTabs();
                })
                .then(() => {
                    if (!settings.get("combinedGroupsSpeed")) {
                        return;
                    }
                    return ResourceCombined.syncGroup(this.model.get("id"), this.model.get("companyId"));
                })
                .then(() => {
                    // Reload the experience model - this will eventually trigger a sync event
                    // inside the PushPublishButtonView, keeping the experience push button in sync.
                    // Set triggeredByServerUpdate to avoid a potential save of the experience
                    // model - see EditorController
                    return this._currentExperience.fetch({triggeredByServerUpdate: true});
                });
        },

        _goToPublish() {
            if (typeof this.options.project === "undefined") {
                Backbone.history.navigate("publish", true);
            } else {
                Backbone.history.navigate("#project/" + this.options.project.get("id") + "/publish", true);
            }
        },

        onClose() {
            this._resizeListener.off();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/editor/GroupNavView.js